import { CommonModule } from "@angular/common";
import { HttpParams } from "@angular/common/http";
import { Component, OnInit } from "@angular/core";
import {
  ApexChart,
  ApexNonAxisChartSeries,
  ApexOptions,
  ApexPlotOptions,
  NgApexchartsModule,
} from "ng-apexcharts";
import { MatIconModule } from "@angular/material/icon";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatSelectModule } from "@angular/material/select";
import { MatOptionModule } from "@angular/material/core";
import { ApiService } from "app/service/api.service";
import { API_ROUTE } from "app/api-routes";
import { ActivityStatusType } from "app/app-constant";
import { BarChartComponent } from "app/layout/common/bar-chart/bar-chart.component";
import { RiskSummaryChartComponent } from "./risk-summary-chart/risk-summary-chart.component";
import { forkJoin } from "rxjs";
import { DonutChartComponent } from "app/layout/common/donut-chart/donut-chart.component";
import { String } from "lodash";
import { Router } from "@angular/router";
import { BaseComponent } from "app/layout/base/base.component";
import { MatMenuModule } from "@angular/material/menu";
import { MatTooltipModule } from "@angular/material/tooltip";

interface StatusIndicator {
  color: string;
  label: string;
  borderColor: string;
}

export type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  title: ApexTitleSubtitle;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  plotOptions: ApexPlotOptions;
  dataLabels: ApexDataLabels;
  stroke: ApexStroke;
  colors: string[];
  fill: any;
};

@Component({
  selector: "app-dashboard",
  standalone: true,
  imports: [
    NgApexchartsModule,
    CommonModule,
    MatIconModule,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    BarChartComponent,
    RiskSummaryChartComponent,
    DonutChartComponent,
    MatMenuModule,
    MatTooltipModule,
  ],
  templateUrl: "./dashboard.component.html",
  styleUrl: "./dashboard.component.scss",
})
export class DashboardComponent extends BaseComponent implements OnInit {
  dashboardData: any;
  chartOptions: {
    series: ApexNonAxisChartSeries;
    chart: ApexChart;
    labels: string[];
    colors: string[];
    plotOptions: ApexPlotOptions;
  };

  chartNewVsReturning: ApexOptions;
  data: any;
  issues: any[] = [
    { severity: "1 - Critical", count: 10, color: "#7F1D1D", width: "208px" },
    { severity: "2 - High", count: 12, color: "#B91C1C", width: "260px" },
    { severity: "3 - Medium", count: 13, color: "#FACC15", width: "277px" },
    { severity: "4 - Low", count: 8, color: "#16A34A", width: "169px" },
  ];
  residualRiskOverview = {
    chart: {
      animations: {
        speed: 400,
        animateGradually: {
          enabled: false,
        },
      },
      fontFamily: "inherit",
      foreColor: "inherit",
      height: "120px",
      type: "donut",
      sparkline: {
        enabled: true,
      },
    },
    colors: ["#E3000C", "#FFC300", "#4390E3", "#7CD169"],
    labels: ["High Risk", "Medium Risk", "Low Risk", "Not Applicable"], // Legend labels

    plotOptions: {
      pie: {
        customScale: 0.9,
        expandOnClick: false,
        donut: {
          size: "60%",
        },
      },
    },
    series: [25, 15, 35, 25], // Example percentages

    states: {
      hover: {
        filter: {
          type: "none",
        },
      },
      active: {
        filter: {
          type: "none",
        },
      },
    },
    tooltip: {
      enabled: true,
      fillSeriesColor: false,
      theme: "dark",
      custom: ({
        seriesIndex,
        w,
      }): string => `<div class="flex items-center h-8 min-h-8 max-h-8 px-3">
                                            <div class="w-3 h-3 rounded-full" style="background-color: ${w.config.colors[seriesIndex]};"></div>
                                            <div class="ml-2 text-md leading-none">${w.config.labels[seriesIndex]}:</div>
                                            <div class="ml-2 text-md font-bold leading-none">${w.config.series[seriesIndex]}%</div>
                                        </div>`,
    },
  };

  riskAssessmentStatus = {
    chart: {
      animations: {
        speed: 400,
        animateGradually: {
          enabled: false,
        },
      },
      fontFamily: "inherit",
      foreColor: "inherit",
      height: "190px",
      type: "donut",
      sparkline: {
        enabled: true,
      },
    },
    colors: ["#E3000C", "#FFC300", "#4390E3", "#7CD169"],
    labels: ["High Risk", "Medium Risk", "Low Risk", "Not Applicable"], // Legend labels

    plotOptions: {
      pie: {
        customScale: 0.9,
        expandOnClick: false,
        donut: {
          size: "60%",
        },
      },
    },
    series: [25, 15, 35, 25], // Example percentages

    states: {
      hover: {
        filter: {
          type: "none",
        },
      },
      active: {
        filter: {
          type: "none",
        },
      },
    },
    tooltip: {
      enabled: true,
      fillSeriesColor: false,
      theme: "dark",
      custom: ({
        seriesIndex,
        w,
      }): string => `<div class="flex items-center h-8 min-h-8 max-h-8 px-3">
                                            <div class="w-3 h-3 rounded-full" style="background-color: ${w.config.colors[seriesIndex]};"></div>
                                            <div class="ml-2 text-md leading-none">${w.config.labels[seriesIndex]}:</div>
                                            <div class="ml-2 text-md font-bold leading-none">${w.config.series[seriesIndex]}%</div>
                                        </div>`,
    },
  };

  vendorCountCategory: any;
  openActivityStatusType: ActivityStatusType = ActivityStatusType.Assessment;

  riskSummary: any;
  riskSummaryType: ActivityStatusType = ActivityStatusType.Assessment;

  riskStatus: any;

  evidenceOverview: any;
  evidenceOverviewType: ActivityStatusType = ActivityStatusType.Assessment;
  pastDueDateOverviewType: ActivityStatusType = ActivityStatusType.Assessment;

  recentActivities: any;
  recentActivityType: ActivityStatusType = ActivityStatusType.Assessment;
  riskStatusType: ActivityStatusType = ActivityStatusType.Assessment;
  riskResponseOverviewType: ActivityStatusType = ActivityStatusType.Assessment;
  inherentRiskOverviewType: ActivityStatusType = ActivityStatusType.Assessment;
  // Overview
  riskResultSummaryData: any;
  residualRiskOverviewData: any;
  inherentRiskOverviewData: any;
  evidenceOverviewData: any;
  pastDueDateOverviewData: any;

  vendorCountType: string = "Category";

  // Re-Assessment Tracker widget state (ported from ihcl-dev). Server-paginated.
  reAssessmentData: any[] = [];
  reAssessmentSearchTerm: string = "";
  reAssessmentStatusFilter: string = "";
  reAssessmentLoading: boolean = false;
  reAssessmentPage: number = 1;
  reAssessmentPageSize: number = 5;
  reAssessmentTotalItems: number = 0;
  private reAssessmentSearchTimeout: any;

  constructor(
    private _apieService: ApiService,
    private _router: Router
  ) {
    super();
  }
  ngOnInit(): void {
    this._initData();
    this.setupKeyboardNavigation();
    this.chartNewVsReturning = {
      chart: {
        type: "donut",
        height: "350px",
      },
      colors: ["#FF5733", "#33FF57", "#3357FF", "#F3FF33", "#33FFF3"],
      labels: ["Item A", "Item B", "Item C", "Item D", "Item E"], // Labels for internal use only
      plotOptions: {
        pie: {
          donut: {
            size: "65%", // Adjust the size of the donut's inner circle
          },
        },
      },
      series: [30, 20, 15, 25, 10], // Values for each segment
      states: {
        hover: {
          filter: {
            type: "darken",
            value: 0.75,
          } as any,
        },
      },
      tooltip: {
        enabled: true,
        theme: "dark",
        y: {
          formatter: function (value) {
            return `${value}%`; // Append percentage to the values
          },
        },
      },
    };

    this.chartOptions = {
      series: [25, 15, 35, 25], // Example percentages
      chart: {
        type: "donut",
        height: 250, // Adjust height as needed
      },
      labels: ["High Risk", "Medium Risk", "Low Risk", "Not Applicable"], // Legend labels
      // colors: ["#E3000C", "#FFC300", "#4390E3", "#7CD169"], // Slice colors
      colors: ["#37647D", "#CE634B", "#418B7C", "#DDBA6A"], // Slice colors
      plotOptions: {
        pie: {
          donut: {
            size: "55%", // Adjust donut hole size
            labels: {
              show: true,
              name: {
                show: false,
              },
              value: {
                show: false,
              },
            },
          },
        },
      },
    };
  }

  private setupKeyboardNavigation(): void {
    document.addEventListener("keydown", (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
        const bars = document.querySelectorAll('[role="graphics-symbol"]');
        const currentFocusIndex = Array.from(bars).findIndex(
          (bar) => document.activeElement === bar
        );

        let newIndex = currentFocusIndex;
        if (event.key === "ArrowLeft") {
          newIndex =
            currentFocusIndex > 0 ? currentFocusIndex - 1 : bars.length - 1;
        } else {
          newIndex =
            currentFocusIndex < bars.length - 1 ? currentFocusIndex + 1 : 0;
        }

        (bars[newIndex] as HTMLElement).focus();
        event.preventDefault();
      }
    });
  }

  onOpenAssessementStatusChange(event: any) {
    this.openActivityStatusType = event.value;
  }

  onRecentActivitiesChange(event: any) {
    this.fetchRecentActivities(event.value);
  }

  onriskStatusChange(event: any) {
    this.riskStatusType = event.value;
    this.fetchRiskStatus(event.value);
  }

  onRiskResponseOverviewChange(event: any) {
    this.riskResponseOverviewType = event.value;
    this.fetchRiskResponseOverview(event.value);
  }

  onInherentRiskOverviewChange(event: any) {
    this.inherentRiskOverviewType = event.value;
    this.fetchInherentRiskOverview(event.value);
  }

  onEvidenceOverviewChange(event: any) {
    this.evidenceOverviewType = event.value;
    this.fetchEvidenceOverview(event.value);
  }

  onPastDueDateOverviewChange(event: any) {
    this.pastDueDateOverviewType = event.value;
    this.fetchPastDueDateOverview(event.value);
  }

  onOpenRiskSummaryChange(e: any) {
    this.riskSummaryType = e.value;
    this.fetchRiskSummary(e.value);
  }
  onVendorCountTypeChange(e: any) {
    this.vendorCountType = e.value;
  }
  private _initData() {
    this._apieService.get(API_ROUTE.DASHBOARD.root).subscribe((res: any) => {
      this.dashboardData = res.data;
    });
    this.fetchRiskSummary(this.riskSummaryType);
    this.fetchRecentActivities(this.recentActivityType);
    this.fetchRiskStatus(this.riskStatusType);
    this.fetchRiskResponseOverview(this.riskResponseOverviewType);
    this.fetchInherentRiskOverview(this.inherentRiskOverviewType);
    this.fetchEvidenceOverview(this.evidenceOverviewType);
    this.fetchPastDueDateOverview(this.pastDueDateOverviewType);
    this.fetchOverviews();
    this.fetchReAssessmentData();
  }

  // ── Re-Assessment Tracker widget ───────────────────────────────────────────

  fetchReAssessmentData(): void {
    this.reAssessmentLoading = true;
    let params = new HttpParams()
      .set("page", this.reAssessmentPage)
      .set("limit", this.reAssessmentPageSize);
    if (this.reAssessmentSearchTerm) {
      params = params.set("search", this.reAssessmentSearchTerm);
    }
    if (this.reAssessmentStatusFilter) {
      params = params.set("status", this.reAssessmentStatusFilter);
    }
    this._apieService.get(API_ROUTE.REASSESSMENT.list, params).subscribe({
      next: (res: any) => {
        const inner = res?.data;
        this.reAssessmentData = inner?.data || [];
        this.reAssessmentTotalItems = inner?.totalItems ?? 0;
        this.reAssessmentPage = inner?.currentPage ?? this.reAssessmentPage;
        this.reAssessmentLoading = false;
      },
      error: () => {
        this.reAssessmentLoading = false;
      },
    });
  }

  onReAssessmentSearch(value: string): void {
    this.reAssessmentSearchTerm = value;
    clearTimeout(this.reAssessmentSearchTimeout);
    // Debounce so we don't fire a request on every keystroke.
    this.reAssessmentSearchTimeout = setTimeout(() => {
      this.reAssessmentPage = 1;
      this.fetchReAssessmentData();
    }, 400);
  }

  onReAssessmentStatusChange(value: string): void {
    this.reAssessmentStatusFilter = value;
    this.reAssessmentPage = 1;
    this.fetchReAssessmentData();
  }

  onReAssessmentPageChange(event: { pageIndex: number; pageSize: number }): void {
    this.reAssessmentPage = event.pageIndex + 1;
    this.reAssessmentPageSize = event.pageSize;
    this.fetchReAssessmentData();
  }

  getReAssessmentStatusColor(status: string): string {
    switch (status?.toLowerCase()) {
      case "on track":  return "#16a34a";
      case "overdue":   return "#dc2626";
      case "due soon":  return "#f97316";
      case "upcoming":  return "#0ea5e9";
      default:          return "#64748b";
    }
  }

  getReAssessmentStatusBgColor(status: string): string {
    switch (status?.toLowerCase()) {
      case "on track":  return "#dcfce7";
      case "overdue":   return "#fee2e2";
      case "due soon":  return "#ffedd5";
      case "upcoming":  return "#e0f2fe";
      default:          return "#f1f5f9";
    }
  }

  redirectToReAssessment(): void {
    this._router.navigate(["/re-assessment-tracker"]);
  }

  redirectToReAssessmentWithStatus(item: any): void {
    this._router.navigate(["/re-assessment-tracker"], {
      queryParams: { status: item.reassessmentStatus },
    });
  }

  fetchRiskSummary(tag: any) {
    this._apieService
      .get(API_ROUTE.DASHBOARD.getRiskSummary(tag))
      .subscribe((res: any) => {
        this.riskSummary = res.data;
      });
  }

  fetchRiskStatus(tag: any) {
    this._apieService
      .get(API_ROUTE.DASHBOARD.getRiskStatus(tag))
      .subscribe((res: any) => {
        this.riskStatus = res.data;
      });
  }

  fetchRecentActivities(tag: string) {
    this._apieService
      .get(API_ROUTE.DASHBOARD.getRecentActivities(tag))
      .subscribe((res: any) => {
        this.recentActivities = res.data;
      });
  }

  fetchRiskResponseOverview(tag: string) {
    this._apieService
      .get(API_ROUTE.DASHBOARD.getRiskResultSummary(tag))
      .subscribe((res: any) => {
        this.riskResultSummaryData = res["data"];
      });
  }

  fetchInherentRiskOverview(tag: string) {
    this._apieService
      .get(API_ROUTE.DASHBOARD.getInherentRiskOverview(tag))
      .subscribe((res: any) => {
        this.inherentRiskOverviewData = res["data"];
      });
  }

  fetchEvidenceOverview(tag: string) {
    this._apieService
      .get(API_ROUTE.DASHBOARD.getEvidenceOverview(tag))
      .subscribe((res: any) => {
        this.evidenceOverviewData = res["data"];
      });
  }

  fetchPastDueDateOverview(tag: string) {
    this._apieService
      .get(API_ROUTE.DASHBOARD.getPastDueDateOverview(tag))
      .subscribe((res: any) => {
        this.pastDueDateOverviewData = res["data"];
      });
  }

  fetchOverviews(): void {
    const apis = {
      residualRiskOverview: this._apieService.get(
        API_ROUTE.DASHBOARD.getResidualRiskOverView
      ),
    };

    forkJoin(apis).subscribe({
      next: ({ residualRiskOverview }) => {
        this.residualRiskOverviewData = residualRiskOverview["data"];
      },
      error: (error) => {
        console.error("Failed to fetch dashboard overviews", error);
      },
    });
  }

  getRiskOverviewCount(label: string): number {
    const item = this.dashboardData?.businessRiskOverviewData?.find(
      (e: any) => e.label === label
    );
    return item?.count ?? 0;
  }

  getStatusClass(status: string): string {
    switch (status?.toLowerCase()) {
      case "initiated":
        return "status-initiated"; // Red
      case "in progress":
        return "status-in-progress"; // Yellow
      case "completed":
        return "status-completed"; // Green
      case "archived":
        return "status-archived"; // Grey
      case "not started":
        return "status-not-started"; // Red
      case "under evaluation":
        return "status-under-evaluation"; // Orange
      case "under review":
        return "status-under-review"; // Blue
      case "rejected":
        return "status-rejected"; // Blue
      default:
        return ""; // No specific styling
    }
  }

  onOpenActStatusBarClick(event: any, url: string) {
    const status = event.label === "In Progress" ? "In-progress" : event.label;
    this._router.navigate(["/" + url], { queryParams: { status } });
  }

  onRiskStatusBarClick(event: any) {
    console.log(event.label);
    let riskType;
    const riskStatus = event.label;
    if (this.riskStatusType === "assessment") riskType = "Assessment";
    else riskType = "Risk Tiering";
    this._router.navigate(["/risk-summary"], {
      queryParams: { riskType, riskStatus },
    });
  }

  onVendorCountByCatClick(event: any) {
    console.log(event.label);
  }

  onVendorCountByDepClick(event: any) {
    console.log(event.label);
  }

  onRiskSummaryClick(event: any) {
    let riskType;
    const riskLevel = event.label;
    if (this.riskSummaryType === "assessment") riskType = "Assessment";
    else riskType = "Risk Tiering";
    this._router.navigate(["/risk-summary"], {
      queryParams: { riskType, riskLevel, riskStatus: "Open" },
    });
  }

  onRiskResOverviewClick(event: any) {
    let riskType, riskResult;
    riskResult = event.label;
    setTimeout(() => {
      if (this.riskResponseOverviewType === "assessment")
        riskType = "Assessment";
      else riskType = "Risk Tiering";

      this._router.navigate(["/risk-summary"], {
        queryParams: { riskType, riskResult },
      });
    }, 100);
  }

  onRiskRiskOverviewChange(event: any) {
    let residualRiskBand;
    residualRiskBand = event.label;
    this._router.navigate(["/assessment"], {
      queryParams: { residualRiskBand },
    });
  }

  onVendorInventoryRedirect(status: string) {
    this._router.navigate(["/vendor-inventory"], {
      queryParams: { status },
    });
  }

  onBusinessRiskRedirect(status: string) {
    this._router.navigate(["/business-risk-dashboard"], {
      queryParams: { status },
    });
  }

  onInherentOverviewChange(event: any) {
    let inherentRiskBand;
    inherentRiskBand = event.label;
    const base =
      this.inherentRiskOverviewType === "assessment"
        ? "/assessment"
        : "/risk-tiering";

    this._router.navigate([base], {
      queryParams: { inherentRiskBand },
    });
  }

  redirectRecentActivity(val: any) {
    const base = val.assessmentId?.startsWith("R-") ? "risk-tiering" : "assessment";
    this._router.navigate([`${base}/form/${val._id}`]);
  }

  isAllZero(data: any[]): boolean {
    if (!data || !data.length) return true;
    return data.every((item) => item?.count === 0);
  }
}
